import { deepEqual as equal } from 'fast-equals';
import React, { createContext, useContext, useEffect } from 'react';

import useStorage from '../useStorage/index.ts';

export type FeatureFlagKey = string;
export type FeatureFlagValue = {
  enabled: boolean;
  value?: string | number | boolean | null;
};
export type FeatureFlagSet = Record<FeatureFlagKey, FeatureFlagValue>;
export const FeatureFlagContext = createContext<FeatureFlagSet>({});

const emptyFeatureFlagSet: FeatureFlagSet = {};
/**
 * Provides a context for feature flags.
 */
export function FeatureFlags({
  children,
  getFlags
}: {
  children: React.ReactNode;
  getFlags: () => Promise<FeatureFlagSet>;
}): React.JSX.Element {
  const [flags, setFlags] = useStorage('flags', emptyFeatureFlagSet);

  if (!getFlags) {
    throw new Error('`getFlags` is required to use the `FeatureFlags` component');
  }

  useEffect(() => {
    setTimeout(
      () => {
        getFlags()
          .then(nextFlags => {
            if (!equal(flags, nextFlags)) {
              setFlags(nextFlags);
            }
          })
          .catch(() => undefined);
      },
      // Retrieve flags after 1s to avoid blocking the initial render
      1000
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>;
}

const unsetFeatureFlag = { enabled: false };

/**
 * Pulls a feature flag from the context.
 * @note This requires the @see {@link FeatureFlags} to be rendered above the component.
 */
export function useFlag(flag: FeatureFlagKey): FeatureFlagValue {
  const featureFlagSet = useContext(FeatureFlagContext);

  if (!featureFlagSet) {
    throw new Error('`useFlag` requires the `FeatureFlags` component to be rendered above it');
  }

  return featureFlagSet[flag] || unsetFeatureFlag;
}
